import type { TimelineEvent } from '@/api/hooks';
import { formatDate, formatWeight, sexLabel } from '@/lib/format';
import { t } from '@/lib/i18n';
import type { LucideIcon } from 'lucide-react';
import {
  Baby,
  Combine,
  Droplets,
  MilkOff,
  Scale,
  Stethoscope,
  Syringe,
  TestTubeDiagonal,
} from 'lucide-react';

export type EventTypeMeta = {
  icon: LucideIcon;
  tone: 'forest' | 'moss' | 'clay' | 'sage';
};

const EVENT_TYPE_META: Record<string, EventTypeMeta> = {
  weighing: { icon: Scale, tone: 'forest' },
  weaning: { icon: MilkOff, tone: 'moss' },
  natural_mount: { icon: Combine, tone: 'clay' },
  insemination: { icon: TestTubeDiagonal, tone: 'clay' },
  pregnancy_diagnosis: { icon: Stethoscope, tone: 'sage' },
  birth: { icon: Baby, tone: 'forest' },
  milk_weighing: { icon: Droplets, tone: 'moss' },
  medication: { icon: Syringe, tone: 'sage' },
};

export function eventTypeMeta(type: string): EventTypeMeta {
  return EVENT_TYPE_META[type] ?? { icon: Scale, tone: 'forest' };
}

export function animalLabel(
  internalNumber: string | null | undefined,
  name: string | null | undefined,
): string | null {
  if (!internalNumber) return null;
  return name ? `${internalNumber} — ${name}` : internalNumber;
}

export type EventDetailLine = {
  label: string;
  value: string;
  highlight?: boolean;
};

export function buildEventDetails(event: TimelineEvent): EventDetailLine[] {
  const lines: EventDetailLine[] = [];

  switch (event.event_type) {
    case 'weighing':
      if (event.weighing_weight_kg != null) {
        lines.push({
          label: t('event.detail.weight'),
          value: formatWeight(event.weighing_weight_kg),
          highlight: true,
        });
      }
      break;
    case 'weaning':
      if (event.weaning_weight_kg != null) {
        lines.push({
          label: t('event.detail.weaning_weight'),
          value: formatWeight(event.weaning_weight_kg),
          highlight: true,
        });
      }
      break;
    case 'milk_weighing': {
      const liters = event.milk_production_liters ?? event.milk_weight_kg;
      if (liters != null) {
        lines.push({
          label: t('event.detail.production'),
          value: `${Number(liters).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} L`,
          highlight: true,
        });
      }
      if (event.milk_milking_shift) {
        lines.push({
          label: 'Turno',
          value:
            event.milk_milking_shift === 'morning'
              ? 'Manhã'
              : event.milk_milking_shift === 'afternoon'
                ? 'Tarde'
                : event.milk_milking_shift === 'night'
                  ? 'Noite'
                  : event.milk_milking_shift,
        });
      }
      if (event.is_drying_off) {
        lines.push({
          label: t('event.detail.drying_off'),
          value: event.drying_off_date ? formatDate(event.drying_off_date) : t('common.yes'),
        });
      }
      break;
    }
    case 'natural_mount':
    case 'insemination':
      if (event.bull_internal_number) {
        lines.push({
          label: t('event.detail.bull'),
          value: animalLabel(event.bull_internal_number, event.bull_animal_name) ?? t('common.dash'),
        });
      }
      break;
    case 'pregnancy_diagnosis':
      if (event.bull_internal_number) {
        lines.push({
          label: t('event.detail.bull'),
          value: animalLabel(event.bull_internal_number, event.bull_animal_name) ?? t('common.dash'),
        });
      }
      if (event.is_pregnant != null) {
        lines.push({
          label: 'Resultado',
          value: event.is_pregnant
            ? `Prenhe · parto previsto ${formatDate(event.expected_calving_date)}`
            : 'Vazia',
          highlight: true,
        });
      }
      if (event.cover_event_id) {
        lines.push({ label: 'Evento cobertura', value: `#${event.cover_event_id}` });
      }
      break;
    case 'birth':
      if (event.calf_internal_number) {
        lines.push({
          label: 'Bezerro(a)',
          value: animalLabel(event.calf_internal_number, event.calf_animal_name) ?? '—',
          highlight: true,
        });
      }
      if (event.calf_sex) {
        lines.push({ label: 'Sexo bezerro', value: sexLabel(event.calf_sex) });
      }
      if (event.bull_internal_number) {
        lines.push({
          label: 'Touro',
          value: animalLabel(event.bull_internal_number, event.bull_animal_name) ?? '—',
        });
      }
      break;
    case 'medication': {
      const kind =
        event.medication_treatment_kind === 'vaccination' ? 'Vacina' : 'Medicamento';
      if (event.medication_product_name) {
        lines.push({
          label: kind,
          value: event.medication_product_name,
          highlight: true,
        });
      }
      if (event.medication_dose) {
        lines.push({ label: 'Dose', value: event.medication_dose });
      }
      if (event.medication_route) {
        lines.push({
          label: 'Via',
          value: medicationRouteLabel(event.medication_route),
        });
      }
      if (event.medication_product_batch) {
        lines.push({ label: 'Lote frasco', value: event.medication_product_batch });
      }
      if (event.medication_next_dose_date) {
        lines.push({
          label: 'Próxima dose',
          value: formatDate(event.medication_next_dose_date),
        });
      }
      break;
    }
  }

  return lines;
}

function medicationRouteLabel(route: string): string {
  switch (route) {
    case 'im':
      return 'IM';
    case 'sc':
      return 'SC';
    case 'iv':
      return 'IV';
    case 'oral':
      return 'Oral';
    case 'topical':
      return 'Tópica';
    default:
      return 'Outra';
  }
}

export function primaryAnimal(event: TimelineEvent): {
  id: number | null;
  label: string | null;
  role: string;
} {
  switch (event.event_type) {
    case 'weighing':
    case 'weaning':
    case 'medication':
      return {
        id: event.subject_animal_id ?? null,
        label: animalLabel(event.subject_internal_number, event.subject_animal_name),
        role: 'Animal',
      };
    case 'milk_weighing':
    case 'natural_mount':
    case 'insemination':
    case 'pregnancy_diagnosis':
      return {
        id: event.cow_animal_id ?? null,
        label: animalLabel(event.cow_internal_number, event.cow_animal_name),
        role: 'Vaca',
      };
    case 'birth':
      return {
        id: event.cow_animal_id ?? null,
        label: animalLabel(event.cow_internal_number, event.cow_animal_name),
        role: 'Mãe',
      };
    default:
      return {
        id: event.subject_animal_id ?? event.cow_animal_id ?? null,
        label:
          animalLabel(event.subject_internal_number, event.subject_animal_name) ??
          animalLabel(event.cow_internal_number, event.cow_animal_name),
        role: 'Animal',
      };
  }
}
